import React, {useEffect, useState} from "react";
import PropTypes from 'prop-types';
import Loading from "components/Loading";
import SimpleTable from "components/complex/simpleTable/SimpleTable";
import Tr from "translations/Translations";
import moment from "libs/moment";
import DateFormats from "constants/DateFormats";
import WorkableIconPreviewButton from "components/button/WorkableIconPreviewButton";
import WorkableIconPrintButton from "components/button/WorkableIconPrintButton";
import {Link, makeStyles, Tooltip, Typography} from "@material-ui/core";
import Pagination from "components/Pagination";
import EmailListService from "services/EmailListService";
import {connect} from "react-redux";
import {setContrastColors} from "redux/actions";
import EmailListSearch from "./EmailListSearch";
import useSearch from "hooks/useSearch";
import {useDidUpdateEffect} from "hooks/useDidUpdateEffect";
import EmailDetailsPopup from "./EmailDetailsPopup";
import WorkableButton from "components/button/WorkableButton";
import GridBlock from "components/GridBlock";

const useStyle = makeStyles(theme => ({
    table: {
        '& .MuiTableCell-head': {
            fontWeight: 700
        }
    },
    buttonColumn: theme.components.table.buttonColumn,
    dateTimeColumn: theme.components.table.dateTimeColumn,
    recColumn: {
        width: 300
    },
    statusColumn: {
        width: 60
    },
    tooltipContent: {
        whiteSpace: 'pre-line'
    },
    recContent: {
        textOverflow: 'ellipsis',
        overflow: 'hidden'
    },
    more: {
        whiteSpace: 'nowrap'
    }
}));

function EmailListSection(
    {
        demandId,
        emailList,
        emailListActions
    }) {

    const classes = useStyle();
    const [searchInProgress, setSearchInProgress] = useState(false);
    const [exportWorking, setExportWorking] = useState(false);
    const [printWorking, setPrintWorking] = useState(0);
    const [emailDetails, setEmailDetails] = useState(null);
    const {searchData, totalCount, setTotalCount, setPaginationConfig, setSearchConfig} = useSearch();


    useDidUpdateEffect(() => {
        if (emailList?.totalCount !== undefined) {
            setTotalCount(emailList.totalCount)
        }
    }, [emailList?.totalCount]);

    useEffect(() => {
        EmailListService.getEmailListActions(demandId);
        return () => {
            EmailListService.clearResources(EmailListService.EMAIL_LIST_RESOURCE_ID)
            EmailListService.clearResources(EmailListService.EMAIL_LIST_ACTIONS_RESOURCE_ID)
        }
    }, [demandId]);

    useEffect(() => {
        setSearchInProgress(true);
        const {pagination, search} = searchData;
        EmailListService.getEmailList(demandId, pagination, search).finally(() => setSearchInProgress(false));
        // eslint-disable-next-line
    }, [searchData]);

    const printEmailDetails = (emailId) => {
        setPrintWorking(emailId);
        EmailListService.printEmailDetails(demandId, emailId, () => setPrintWorking(0));
    }

    const getHeaders = (emailList) => {
        const arr = [];

        arr.push(Tr('subject'));
        arr.push(Tr('recipients'));
        arr.push(Tr('status'));
        arr.push(Tr('send_date'));
        arr.push('');
        arr.push('');
        return arr;
    }

    const getItems = ({items}) => {

        const rows = [];

        items.forEach(({identity, to, cc, bcc, subject, insertDate}, i, array) => {

            const recipients = [...to, ...cc, ...bcc];
            const arr = [];
            arr.push({
                value: subject
            });

            arr.push({
                value: <Typography className={classes.recContent}>{recipients[0] + " "}
                    {(recipients.length > 2) && <Tooltip title={<span className={classes.tooltipContent}>{recipients.join('\n')}</span>}>
                        <Typography className={classes.more} component='span'
                                    color='secondary'>{' ' + Tr('and_x_more', {more: recipients.length - 1})}</Typography></Tooltip>}
                </Typography>
            });

            arr.push({
                value: <Link component="button" variant="body2" onClick={() => setEmailDetails({id: identity, detailed: false})}>{Tr('details')}</Link>
            });

            arr.push({
                value: insertDate ? moment(insertDate).format(DateFormats.DATETIME_FORMAT) : null
            });

            arr.push({
                value: <WorkableIconPrintButton onClick={() => {
                    printEmailDetails(identity);
                }} working={printWorking === identity} />
            });

            arr.push({
                value: <WorkableIconPreviewButton onClick={() => {
                    setEmailDetails({id: identity, detailed: true});
                }}/>
            });

            rows.push(arr);
        })
        return rows;
    };

    const getColumnClasses = () => {
        const arr = [];

        arr.push(undefined);
        arr.push(classes.recColumn);
        arr.push(classes.statusColumn);
        arr.push(classes.dateTimeColumn);
        arr.push(classes.buttonColumn);
        arr.push(classes.buttonColumn);
        return arr;
    };

    const doExport = () => {
        setExportWorking(true);
        EmailListService.doExportEmails(emailListActions.exportEmailList.url, () => setExportWorking(false));
    }

    if (!emailList || !emailListActions) return <Loading/>
    return (
        <>
            {!!emailListActions.exportEmailList && <GridBlock justify='flex-end'>
                <WorkableButton working={exportWorking} onClick={doExport}>{Tr('export_email_list')}</WorkableButton>
            </GridBlock>
            }

            <EmailListSearch doSearch={setSearchConfig} searchInProgress={searchInProgress}/>
            <SimpleTable
                headers={getHeaders(emailList)}
                items={getItems(emailList)}
                columnClasses={getColumnClasses()}
                // cellClassName={classes.cell}
                className={classes.table}
            />
            <Pagination totalCount={totalCount} disabled={searchInProgress} config={searchData.pagination} setConfig={setPaginationConfig}/>
            {!!emailDetails &&
            <EmailDetailsPopup detailed={emailDetails.detailed} demandId={demandId} emailId={emailDetails.id} onClose={() => setEmailDetails(null)}/>}
        </>
    )
}


EmailListSection.propTypes = {
    classes: PropTypes.any,
}

export default connect((state) => ({
    emailList: state.resources[EmailListService.EMAIL_LIST_RESOURCE_ID],
    emailListActions: state.resources[EmailListService.EMAIL_LIST_ACTIONS_RESOURCE_ID],
}), {
    setContrastColors
})(EmailListSection);